import { createFileRoute } from '@tanstack/react-router';
import { LiveboardEmbed as LiveboardEmbedType } from '@thoughtspot/visual-embed-sdk';
import { LiveboardEmbed, useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { RefObject } from 'react';
import { useUserAppViewConfig } from '@/api/queries/useSdkTokenQuery';

interface RouteParams {
  id: string;
}

interface ParsedParams {
  id: string;
}

/**
 * DEEP LINK ROUTE: Liveboard/Dashboard Viewer
 *
 * This route serves as a direct access point for sharing/bookmarking specific ThoughtSpot liveboards (dashboards).
 * Users typically land here via:
 * - Shared liveboard links (email, Slack, Teams, etc.)
 * - Bookmarks
 * - External integrations
 * - Visualization links within liveboards
 *
 * URL Pattern: /dashboard/pinboard/{id}
 * Example: /dashboard/pinboard/d084c256-e284-4fc4-b80c-111cb606449a
 *
 * This route uses a direct LiveboardEmbed component with its own independent iframe,
 * not the prerendered shared iframe from PrerenderedThoughtSpotProvider.
 */
export const Route = createFileRoute('/dashboard/_deep-links/pinboard/$id')<{
  params: RouteParams;
}>({
  component: RouteComponent,
  params: {
    parse: (params: Record<string, string>): ParsedParams => {
      const { id } = params;

      return {
        id,
      };
    },
  },
});

function RouteComponent() {
  const ref = useEmbedRef() as RefObject<LiveboardEmbedType>;
  const { id } = Route.useParams();
  const userAppViewConfig = useUserAppViewConfig();

  return (
    <LiveboardEmbed
      showLiveboardTitle={true}
      ref={ref}
      liveboardV2={true}
      isLiveboardMasterpiecesEnabled={true}
      // TODO: waiting for TS support verification whether the flag also needs to be explicitly passed on those deep-link routes
      // enableCustomColumnGroups={true}
      // additionalFlags={{ enableCustomColumnGroups: true }}
      liveboardId={id}
      className="h-screen w-full"
      {...(userAppViewConfig?.hiddenActions && {
        hiddenActions: userAppViewConfig.hiddenActions,
      })}
    />
  );
}
